import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError, AxiosResponse } from 'axios'
import { fetchDataFromApi } from '../services/fetchDataFromApi'
import { DashBoardEventDto } from '../types/DashBoardEventDto.tsx'
import { EventDetailsDto } from '../types/EventDetailsDto.tsx'

interface ApiState {
  allEventCount: number
  eventsInTimespan: number
  eventDtos: DashBoardEventDto[] | undefined
  selectedEventDashboard: DashBoardEventDto | null
  selectedEvent: EventDetailsDto | null
  selectedYear: number
  url: string
  loading: 'idle' | 'pending' | 'succeeded' | 'failed'
  error: string | null
  statusCode: number | null
}

const initialState: ApiState = {
  allEventCount: 0,
  eventsInTimespan: 0,
  eventDtos: undefined,
  selectedYear: new Date().getFullYear(),
  selectedEventDashboard: null,
  selectedEvent: null,
  url: '',
  loading: 'idle',
  error: null,
  statusCode: null,
}

export interface FetchApiDataOptions {
  url: string
  token: string
  method?: string
  bodyData?: object
}

// Eventliste und meta data (counter)
export const fetchEventsFromApiData = createAsyncThunk('api/fetchEventsFromApiData', async ({ url, token }: FetchApiDataOptions) => {
  try {
    const data: AxiosResponse | AxiosError = await fetchDataFromApi(url, token)
    return data
  } catch (error: any) {
    // Fehlerbehandlung
  }
})

// Einzelnes Event
export const fetchEventFromApiData = createAsyncThunk('api/fetchEventFromApiData', async ({ url, token }: FetchApiDataOptions) => {
  try {
    const data: AxiosResponse | AxiosError = await fetchDataFromApi(url, token)
    return data
  } catch (error: any) {
    // Fehlerbehandlung
  }
})

// Sperrzeiten add/update
export const createOrUpdateSperrzeit = createAsyncThunk('api/createOrUpdateSperrzeit', async ({ url, token, bodyData }: FetchApiDataOptions) => {
  try {
    const data: AxiosResponse | AxiosError = await fetchDataFromApi(url, token, 'POST', 'json', bodyData)
    return data
  } catch (error: any) {
    // Fehlerbehandlung
  }
})

const eventSlice = createSlice({
  name: 'eventStore',
  initialState,
  reducers: {
    addEvent: (state, action) => {
      if (state.eventDtos)
        state.eventDtos = [...state.eventDtos, action.payload]
    },
    removeEvent: (state, action) => {
      if (state.eventDtos)
        state.eventDtos = state.eventDtos.filter((event) => event.uniqueDocId !== action.payload.uniqueDocId)
    },
    setSelectedEvent: (state, action) => {
      state.selectedEvent = action.payload
    },
    setSelectedDashboardEvent: (state, action) => {
      state.selectedEventDashboard = action.payload
    },
    setSelectedYear: (state, action) => {
      state.selectedYear = action.payload
    },
    clearEventList: (state) => {
      state.eventDtos = undefined
    },
    clearSelectedEvent: (state) => {
      state.selectedEvent = null
    },
    clearSelectedDashboardEvent: (state) => {
      state.selectedEventDashboard = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Event liste + counts
      .addCase(fetchEventsFromApiData.pending, (state) => {
        state.loading = 'pending'
      })
      .addCase(fetchEventsFromApiData.fulfilled, (state, action) => {
        const result = action.payload
        if (result instanceof AxiosError) {
          if (result?.status < 200 || result.status >= 300) {
            state.statusCode = result.status
          } else if (result.code === 'ERR_NETWORK') {
            state.statusCode = 503
          } else if (result.code === 'ERR_BAD_REQUEST') {
            state.statusCode = 403
          } else {
            state.statusCode = result.status
          }
          state.loading = 'failed'
          state.error = result.statusText
        } else if (result) {
          state.loading = 'succeeded'
          state.allEventCount = action.payload?.data.allEventCount
          state.eventsInTimespan = action.payload?.data.eventsInTimespan
          state.eventDtos = action.payload?.data.eventDtos
          state.statusCode = result.status
        }
      })
      .addCase(fetchEventsFromApiData.rejected, (state, action) => {
        state.loading = 'failed'
        state.error = action.error.message || 'An error occurred'
      })

      // Einzel Event
      .addCase(fetchEventFromApiData.pending, (state) => {
        state.loading = 'pending'
      })
      .addCase(fetchEventFromApiData.fulfilled, (state, action) => {
        const result = action.payload
        if (result instanceof AxiosError) {
          if (result?.status < 200 || result.status >= 300) {
            state.statusCode = result.status
          } else if (result.code === 'ERR_NETWORK') {
            state.statusCode = 503
          } else if (result.code === 'ERR_BAD_REQUEST') {
            state.statusCode = 403
          } else {
            state.statusCode = result.status
          }
          state.loading = 'failed'
          state.error = result.statusText
        } else if (result) {
          state.loading = 'succeeded'
          state.selectedEvent = action.payload?.data
        }
      })
      .addCase(fetchEventFromApiData.rejected, (state, action) => {
        state.loading = 'failed'
        state.error = action.error.message || 'An error occurred'
      })

      // Sperrzeit add/update
      .addCase(createOrUpdateSperrzeit.pending, (state) => {
        state.loading = 'pending'
      })
      .addCase(createOrUpdateSperrzeit.fulfilled, (state, action) => {
        const result = action.payload
        if (result instanceof AxiosError) {
          if (result?.status < 200 || result.status >= 300) {
            state.statusCode = result.status
          } else if (result.code === 'ERR_NETWORK') {
            state.statusCode = 503
          } else if (result.code === 'ERR_BAD_REQUEST') {
            state.statusCode = 403
          } else {
            state.statusCode = result.status
          }
          state.loading = 'failed'
          state.error = result.statusText
        } else if (result) {

          state.loading = 'succeeded'
          if (state.eventDtos) {
            const index = state.eventDtos.findIndex((item) => item.uniqueDocId === action.payload?.data?.uniqueDocId)
            if (index !== -1) {
              // Dublikat gefunden -> update/replace
              if (state.eventDtos)
                state.eventDtos[index] = { ...state.eventDtos[index], ...action.payload?.data }
            } else {
              // Keines gefunden -> add
              state.eventDtos = [...state.eventDtos, action.payload?.data]
            }
          }
        }
      })
      .addCase(createOrUpdateSperrzeit.rejected, (state, action) => {
        state.loading = 'failed'
        state.error = action.error.message || 'An error occurred'
      })
  },
})

export const { setSelectedYear, setSelectedEvent, setSelectedDashboardEvent, clearEventList, clearSelectedEvent, clearSelectedDashboardEvent, addEvent, removeEvent } = eventSlice.actions

export default eventSlice.reducer
